import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Download, FileText, Shield } from "lucide-react";
import { useAuth } from "@/contexts/auth"; // now correct via barrel
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface DataSummaryResponse {
  data_categories: {
    [key: string]: {
      count?: number;
      includes: string;
      [key: string]: any;
    };
  };
  estimated_size_kb: number;
  last_activity?: string;
}

export function GdprDataExport({ compact = false }: { compact?: boolean } = {}) {
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingSummary, setIsLoadingSummary] = useState(false);

  const handleDownloadData = async () => {
    if (!user?.id) return;

    setIsLoading(true);
    try {
      const { data, error } = await supabase.rpc("get_user_complete_data", {
        user_id_param: user.id,
      });

      if (error) throw error;

      const blob = new Blob([JSON.stringify(data, null, 2)], {
        type: "application/json",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `nationcard-data-export-${new Date().toISOString().split("T")[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success("Your data has been downloaded successfully");
    } catch (error) {
      console.error("Error downloading data:", error);
      toast.error("Failed to download your data. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewSummary = async () => {
    if (!user?.id) return;

    setIsLoadingSummary(true);
    try {
      const { data, error } = await supabase.rpc(
        "get_user_data_export_summary",
        {
          user_id_param: user.id,
        }
      );

      if (error || !data) throw error || new Error("No summary data returned");

      const summaryData = data as unknown as DataSummaryResponse;

      const summaryText = Object.entries(summaryData.data_categories)
        .map(([category, info]) => {
          if (info.count !== undefined) {
            return `${category.replace(/_/g, " ").toUpperCase()}: ${info.count} records - ${info.includes}`;
          }
          return `${category.replace(/_/g, " ").toUpperCase()}: ${JSON.stringify(info)}`;
        })
        .join("\n");

      alert(
        `Data Summary:\n\n${summaryText}\n\nEstimated size: ${summaryData.estimated_size_kb}KB`
      );
    } catch (error) {
      console.error("Error getting data summary:", error);
      toast.error("Failed to get data summary. Please try again.");
    } finally {
      setIsLoadingSummary(false);
    }
  };

  return (
    <Card className={compact ? "border-muted" : ""}>
      {!compact && (
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            GDPR Data Export
          </CardTitle>
        </CardHeader>
      )}
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Under GDPR, you can download all personal data we hold about you. This is available in machine-readable format.
        </p>
        <div className="flex flex-col sm:flex-row gap-3">
          <Button
            onClick={handleViewSummary}
            variant="purple"
            disabled={isLoadingSummary}
            className="flex items-center gap-2"
          >
            <FileText className="h-4 w-4" />
            {isLoadingSummary ? "Loading..." : "View Data Summary"}
          </Button>
          <Button
            onClick={handleDownloadData}
            disabled={isLoading}
            className="flex items-center gap-2 bg-nation-blue hover:bg-nation-blue-dark text-white shadow-md"
          >
            <Download className="h-4 w-4" />
            {isLoading ? "Preparing Download..." : "Download My Data"}
          </Button>
        </div>
        {compact && (
          <div className="text-xs text-muted-foreground space-y-1">
            <p>
              • The export file contains all personal data and is generated in real-time
            </p>
            <p>
              • The export includes metadata about data processing
            </p>
          </div>
        )}
        {!compact && (
          <div className="text-xs text-muted-foreground space-y-1">
            <p>
              • Your data export includes all personal information, voting
              history, comments, and interactions
            </p>
            <p>
              • The download file is in JSON format and contains metadata about
              data processing
            </p>
            <p>
              • This export is generated in real-time and reflects your current
              data
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
